import { EventEmitter, Injectable, OnDestroy, Output } from '@angular/core';
import { transaction } from '@datorama/akita';
import { MessagesStore } from './messages.store';
import { catchError, map } from 'rxjs/operators';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { TextMessage } from './text-message.model';
import { MessageInterface } from './message-interface.model';
import { FileMessage } from './file-message.model';
import { ApiBaseService } from "../../common/providers/api-base-service";
import { Paginator, PaginatorData } from "../../common/models/paginator";
import { CurrentUser } from "../../common/providers/current-user";

@Injectable({ providedIn: 'root' })
export class MessagesService implements OnDestroy {
    @Output()
    public messagesUpdatedEvent: EventEmitter<any> = new EventEmitter<any>();
    private subscriptions: Subscription[] = [];

    constructor(
        private store: MessagesStore,
        private currentUser: CurrentUser,
        private api: ApiBaseService,
    ) { }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public get(ownerId:number, discussionId:number, page = 1, scrollPosition: null | string, loading = true) {
        this.store.setLoading(loading);

        this.subscriptions.push(
            this.findAllByDiscussion(ownerId, discussionId, page).subscribe((res: Paginator) => this.updateMessages(res, scrollPosition))
        );
    }

    public add(message: MessageInterface) {
        this.store.setLoading(true);
        this.store.add([message]);
        this.store.setLoading(false);

        this.messagesUpdatedEvent.emit('bottom');
    }

    public resetStore() {
        this.store.reset();
        // this.store.destroy();
    }

    /**
     * Send a text message
     *
     * @param userId
     * @param discussionId
     * @param payload
     */
    public sendTextMessage(userId: number, discussionId: number, payload: Object = {}): Observable<TextMessage> {
        return this.api.call('post',`users/${userId}/chat/discussions/${discussionId}/messages`, payload, this.currentUser.getAccessToken())
          .pipe(
            map((res) => TextMessage.fromAPI(res))
          )
    }

    public sendFileMessage(userId: number, discussionId: number, participantId: number, files: Set<File>): { [key: string]: { observable: Observable<any> } } {
        // this will be the our resulting map
        const status: { [key: string]: { observable: Observable<any> } } = {};
        const url = `users/${userId}/chat/discussions/${discussionId}/files`;

        files.forEach((file) => {
            // create a new progress-subject for every file
            const progress = new Subject<any>();

            const fd = new FormData();
            fd.append('file', file, file.name);
            fd.append('participant', participantId.toString());


            // Inspecter le contenu de FormData
            fd.forEach((value, key) => {
              // console.log(key, value);
            });

            this.subscriptions.push(
                this.api.call('post',url, fd, this.currentUser.getAccessToken(), false).subscribe({
                    next: (res) => {
                        progress.next({file: FileMessage.fromAPI(res), errors: []});
                        progress.complete();
                    },
                    error: (err) => {
                        progress.error(err);
                    },
                }),
            );

            // Save every progress-observable in a map of all observables
            status[file.name] = {
                observable: progress.asObservable()
            };
        });

        return status;
    }

    /**
     * Find all messages for a discussion
     *
     * @param userId
     * @param discussionId
     * @param page
     */
    public findAllByDiscussion(userId: number, discussionId: number, page = 1): Observable<Paginator> {
        return this.api.call('get',`users/${userId}/chat/discussions/${discussionId}/messages?page=${page}`, {}, this.currentUser.getAccessToken())
            .pipe(
                map((paginator: PaginatorData) => {
                  return Paginator.fromAPI(paginator);
                })
            )
        ;
    }

  public download(userId: number, discussionId: number, messageId: number): Observable<any> {
    return this.api.call(
      'get',
      `users/${userId}/chat/discussions/${discussionId}/messages/${messageId}/file`,
      {
        responseType: 'blob'
      },
      {
        accessToken: this.currentUser.getAccessToken(),
        resultType: 'file'
      },
      false,
    ).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

    @transaction()
    private updateMessages(paginator: Paginator, scrollPosition: null | string) {
        const hasMore = paginator.current_page < paginator.last_page;
        const nextPage = paginator.current_page + 1;

        this.store.add(paginator.data.map((res) => TextMessage.fromAPI(res)));
        this.store.updatePage({ hasMore, page: nextPage });
        this.store.setLoading(false);

        this.messagesUpdatedEvent.emit(scrollPosition);
    }
}
